import { SetStateFunction } from "constants/interfaces";
import React, { useEffect } from "react";

export interface InitialDataInterface {
  content: React.ReactNode | string;
  isClosableByOverlay?: boolean;
  onClose?: () => Promise<any>;
}

export type currentRouteOptionsI = {
  route: string;
  callback?: () => void;
  hasSearchInput?: boolean;
  onSearch: (value: string) => void;
  isSearching?: boolean;
  closeDrawerOnRouteChange?: boolean;
};

export const initialContent: InitialDataInterface = {
  content: "",
  isClosableByOverlay: true,
};

export interface UIContextStateI {
  // Overlay
  isOverlayOpen: boolean;
  setOverlayOpen: (isOverlayOpen: boolean) => void;
  // Modal
  modalData: InitialDataInterface;
  nestedModalData: InitialDataInterface;
  openModal: (data: InitialDataInterface) => void;
  closeModal: () => void;
  openNestedModal: (data: InitialDataInterface) => void;
  closeNestedModal: () => void;
  isModalOpen: boolean;
  isNestedModalOpen: boolean;
  // Sidebar
  isSidebarOpen: boolean;
  toggleSidebar: (state?: boolean) => void;
  sidebarData?: React.ReactNode;
  setSidebarData: SetStateFunction<React.ReactNode | undefined>;
  // Drawer
  isLeftDrawerOpen: boolean;
  isRightDrawerOpen: boolean;
  leftDrawerData: InitialDataInterface;
  rightDrawerData: InitialDataInterface;
  openLeftDrawer: (data: InitialDataInterface) => void;
  openRightDrawer: (data: InitialDataInterface) => void;
  closeLeftDrawer: () => void;
  closeRightDrawer: () => void;
  resetAll: () => void;
  currentRouteOptions: currentRouteOptionsI;
  setRouteOptions: (e: any) => void;
  resetCurrentRouteOptions: () => void;
  overlayFunction: any;
  isSidebarShrunk: boolean;
  setSidebarShrunk: (isSidebarShrunk: boolean) => void;
  isActionBarOpen: boolean;
  openActionBar: (
    data: InitialDataInterface & {
      containerClassName?: string;
      containerTopElementClassName?: string;
    },
  ) => void;
  closeActionBar: () => void;
  actionBarData: InitialDataInterface & {
    containerClassName?: string;
    containerTopElementClassName?: string;
  };
}

export const initialState: UIContextStateI = {
  // Overlay
  isOverlayOpen: false,
  setOverlayOpen: () => {},
  // Modal
  modalData: initialContent,
  nestedModalData: initialContent,
  openModal: () => {},
  closeModal: () => {},
  openNestedModal: () => {},
  closeNestedModal: () => {},
  isModalOpen: false,
  isNestedModalOpen: false,
  // Sidebar
  isSidebarOpen: false,
  toggleSidebar: () => {},
  sidebarData: undefined,
  setSidebarData: () => {},
  // Drawer
  isLeftDrawerOpen: false,
  isRightDrawerOpen: false,
  leftDrawerData: initialContent,
  rightDrawerData: initialContent,
  openLeftDrawer: () => {},
  openRightDrawer: () => {},
  closeLeftDrawer: () => {},
  closeRightDrawer: () => {},
  resetAll: () => {},
  currentRouteOptions: {
    route: "",
    callback: undefined,
    hasSearchInput: false,
    onSearch: () => {},
    closeDrawerOnRouteChange: true,
  },
  setRouteOptions: () => {},
  resetCurrentRouteOptions: () => {},
  overlayFunction: () => {},
  isSidebarShrunk: false,
  setSidebarShrunk: () => {},
  isActionBarOpen: false,
  openActionBar: () => {},
  closeActionBar: () => {},
  actionBarData: initialContent,
};

const UIContext = React.createContext<UIContextStateI>(initialState);

export const useUI = () => React.useContext(UIContext);

export const useSetCurrentRouteOptions = () => {
  const { setRouteOptions, resetCurrentRouteOptions, resetAll } = useUI();
  useEffect(() => {
    return () => {
      // @EXPERIMENTAL: reset all UI state on route change
      resetAll();
      // -----------------------------------------------
      resetCurrentRouteOptions();
    };
  }, []);

  const setRouteOptionsCallback = React.useCallback(
    (options: currentRouteOptionsI) => {
      setRouteOptions(options);
    },
    [setRouteOptions],
  );

  return setRouteOptionsCallback;
};

export default UIContext;
